import React, { useState, useEffect } from 'react';
import { getContactPageData } from '../services/contactService';
import { renderSafeText, getStrapiMedia } from '../lib/strapi';
import { MapPin, Send, Globe } from 'lucide-react';

const Contact = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState({ status: 'idle', message: '' }); // idle, sending, success, error

  useEffect(() => {
    const fetchData = async () => {
      const result = await getContactPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ status: 'sending', message: 'Sending your inquiry...' });

    const htmlMessage = `<html>
        <body style="font-family: Arial, sans-serif; line-height: 1.5;">
            <h2 style="color:#2c3e50;">New Website Inquiry</h2>
            <p>
            Dear Widya Team,
            </p>
            <p>
            <strong>${formData.name}</strong> has submitted an inquiry via the website contact form:
            </p>
            <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%;">
            <tr>
                <td style="width: 25%;"><strong>Full Name</strong></td>
                <td>${formData.name}</td>
            </tr>
            <tr>
                <td><strong>Email Address</strong></td>
                <td>${formData.email}</td>
            </tr>
            <tr>
                <td><strong>Company</strong></td>
                <td>${formData.company || 'N/A'}</td>
            </tr>
            <tr>
                <td><strong>Message</strong></td>
                <td>${formData.message}</td>
            </tr>
            </table>
            <p>
            Please review and respond to the inquiry appropriately.
            </p>
            <p>Thank you,<br>
            Widya System Notification</p>
        </body>
        </html>`;

    try {
      const emailApiUrl = process.env.REACT_APP_WIDYA_EMAIL_URL;
      const apiToken = process.env.REACT_APP_WIDYA_EMAIL_TOKEN;
      const TO_EMAIL = "info@widya.io"; 

      if (!emailApiUrl || !apiToken) throw new Error("Email Service Configuration Missing");

      const payload = {
        token: apiToken,
        to: TO_EMAIL,
        addcc: "",
        addbcc: "",
        subject: `New Inquiry from ${formData.name}`,
        message: htmlMessage
      };

      const response = await fetch(emailApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFormStatus({ status: 'success', message: 'Thank you! Your inquiry has been sent.' });
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Failed to send email.');
      }
    } catch (error) {
      console.error(error);
      setFormStatus({ status: 'error', message: 'Something went wrong. Please try again later.' });
    }
  };

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Contact Details...</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section 
        className="relative overflow-hidden min-h-[40vh] lg:min-h-[60vh] flex items-center justify-center bg-widya-footer bg-cover bg-center"
        style={{ backgroundImage: data.hero_image ? `url(${getStrapiMedia(data.hero_image)})` : 'none' }}
      >
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1100px]">
          <h1 className="text-[40px] md:text-[56px] lg:text-[72px] font-bold tracking-tight leading-[1.1] max-w-4xl mx-auto font-heading text-white mb-6 drop-shadow-xl">
            {renderSafeText(data.hero_title)}
          </h1>
          <p className="text-[14px] md:text-[16px] text-widya-green font-black uppercase tracking-[0.3em] font-heading drop-shadow-md">
            {renderSafeText(data.hero_subtitle)}
          </p>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="container mx-auto max-w-[1100px] grid lg:grid-cols-2 gap-16">
          <div className="bg-gray-50 p-8 md:p-12 rounded-[2.5rem] border border-gray-100">
            <h2 className="text-2xl font-bold text-widya-charcoal mb-8 flex items-center gap-2 font-heading">
              <Send className="text-widya-green" size={24} /> Send us a Message
            </h2>
            <form className="space-y-6" onSubmit={handleFormSubmit}>
              {formStatus.status === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-medium">
                  {formStatus.message}
                </div>
              )}
              {formStatus.status === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                  {formStatus.message}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Full Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="john@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Company Name</label>
                <input type="text" name="company" value={formData.company} onChange={handleInputChange} className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="AgriCorp Ltd" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Your Message</label>
                <textarea rows="5" name="message" value={formData.message} onChange={handleInputChange} required className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all resize-none" placeholder="How can we help you?"></textarea>
              </div>
              <button type="submit" disabled={formStatus.status === 'sending'} className="w-full bg-widya-green text-white font-bold py-4 rounded-xl hover:bg-widya-green-dark transition-all shadow-lg shadow-widya-green/20 disabled:opacity-70 disabled:cursor-not-allowed">
                {formStatus.status === 'sending' ? 'Sending...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>

          <div className="space-y-12">
            <div>
              <h3 className="text-sm font-extrabold text-widya-green uppercase tracking-[0.2em] mb-4">Head Office</h3>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-widya-green/10 text-widya-green rounded-lg shrink-0">
                  <Globe size={24} />
                </div>
                <div>
                  <p className="text-xl font-bold text-widya-charcoal mb-1">{renderSafeText(data.primary_contact_label)}</p>
                  <a href={`mailto:${data.primary_email}`} className="text-widya-body hover:text-widya-green transition-colors">
                    {renderSafeText(data.primary_email)}
                  </a>
                </div>
              </div>
            </div>

            <hr className="border-gray-100" />

            <div>
              <h3 className="text-sm font-extrabold text-gray-400 uppercase tracking-[0.2em] mb-8">International Presence</h3>
              <div className="grid sm:grid-cols-2 gap-8">
                {data.office_addresses?.map((office, i) => (
                  <div key={i} className="group">
                    <div className="flex items-center gap-2 mb-3 text-widya-charcoal font-bold">
                      <MapPin size={18} className="text-widya-green" />
                      {renderSafeText(office.city)}
                    </div>
                    <div className="text-sm text-widya-body leading-relaxed pl-7">
                      <p>{renderSafeText(office.address_line_1)}</p>
                      <p>{renderSafeText(office.address_line_2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;